import React, { useCallback, useEffect, useState } from 'react'
import { Modal } from '@mui/material'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { Button } from 'src/components/shared/Button'
import { DownloadContractorFile, FetchContractorFile } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import { useDispatch } from 'react-redux'
import { ContractorFile, ContractorFileDocument } from 'src/interfaces/file'
import { GSIcon } from 'src/components/shared/GSIcon'
import { getMimeTypeFromExtension } from 'src/utils/fileDownload'
import { FullScreenLoader } from 'src/components/shared/FullScreenLoader'

interface Props {
  isOpen: boolean
  contractorId: string
  contractorFile: ContractorFile
  onClose(): void
}

const IMAGE_FILE_TYPES = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'webp']

export const ContractorFilePreviewModalModal = ({
  isOpen,
  contractorId,
  contractorFile,
  onClose,
}: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)
  const [contractorFileDocument, setContractorFileDocument] =
    useState<ContractorFileDocument | null>(null)

  const fetchContractorFileDocument = useCallback(async () => {
    try {
      const fileDocument: ContractorFileDocument = await createAsyncAction(
        dispatch,
        FetchContractorFile.request({
          contractorId,
          fileId: contractorFile.id,
        }),
      )

      setContractorFileDocument(fileDocument)
    } catch (err) {
      handleApiError(err)
    }
  }, [contractorId, contractorFile.id])

  useEffect(() => {
    if (contractorFileDocument) {
      return
    }

    fetchContractorFileDocument()
  }, [contractorFileDocument, fetchContractorFileDocument])

  const handleClickDownload = useCallback(async () => {
    if (!contractorFileDocument) {
      return
    }

    try {
      setIsLoading(true)

      const buffer: ArrayBuffer = await createAsyncAction(
        dispatch,
        DownloadContractorFile.request({
          contractorId,
          fileId: contractorFile.id,
        }),
      )
      const blob = new Blob([buffer], {
        type: getMimeTypeFromExtension(contractorFileDocument.document.extension || 'pdf'),
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${contractorFileDocument.document.fileName}`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [contractorId])

  if (!contractorFileDocument) {
    return <FullScreenLoader />
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="file-modal-title"
      aria-describedby="file-modal-description"
      css={tw`fixed inset-0 flex items-center justify-center`}
    >
      <div
        css={[
          tw`bg-white rounded-lg w-[724px] max-h-[95vh] flex flex-col`,
          !IMAGE_FILE_TYPES.includes(contractorFileDocument.document.extension as string) &&
            tw`h-full`,
        ]}
      >
        <ModalHeader className={tw`h-11`} title="Contractor file" onClose={onClose} />
        {!IMAGE_FILE_TYPES.includes(contractorFileDocument.document.extension as string) ? (
          <object
            data={`${contractorFileDocument.document.filePath}#toolbar=0`}
            type="application/pdf"
            css={tw`w-full h-full min-h-[100px] overflow-y-auto`}
          />
        ) : (
          <div css={tw`flex flex-col h-full min-h-0`}>
            <div css={tw`overflow-y-auto flex-1 min-h-0`}>
              <img
                src={contractorFileDocument.document.filePath}
                css={tw`w-full h-full object-cover`}
              />
            </div>
          </div>
        )}
        <div
          css={tw`flex justify-end items-center px-4 py-3 border-0 border-t border-solid  border-[#EDEDED]`}
        >
          <Button
            size="small"
            color="grey"
            loading={isLoading}
            leftIcon={<GSIcon iconName="gs_circle_download" width={20} />}
            onClick={handleClickDownload}
          >
            Download
          </Button>
        </div>
      </div>
    </Modal>
  )
}
